import React, { useState, useEffect } from 'react';
import { useImmer } from 'use-immer';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeature,
  CharacterLanguageFeature,
  LANGUAGE_OPTIONS,
  LanguageFeature,
  LANGUAGES
} from "@dndModels";

import DisplayObjects from "@dndComponents/display/DisplayObjects";
import CheckBox from "@components/input/CheckBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

interface Props {
  character: Character;
  obj: CharacterFeature;
  onChange: (changed: CharacterLanguageFeature) => void;
  color?: string; 
}

export default function CharacterLanguageFeatureInput({ 
  character,
  obj,
  onChange,
  color = ""
}: Props) {
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [ignore_type_limitations, setIgnoreTypeLimitations] = useImmer(() => {
    let ignore = false;
    const language_feature = obj.floating_feature;
    if (language_feature instanceof LanguageFeature && language_feature.type !== "Specific" && obj.feature_options[0] instanceof CharacterLanguageFeature) {
      const character_language_feature = obj.feature_options[0];
      const non_type_languages = language_feature.type === "Standard" ? 
        LANGUAGES.filter(o => o.type !== "Common" && o.type !== "Exotic" && character_language_feature.language_ids.includes(o._id)) :
        LANGUAGES.filter(o => o.type !== language_feature.type && character_language_feature.language_ids.includes(o._id));
      ignore = non_type_languages.length > 0;
    }
    return ignore;
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  // debugger;
  if (obj.floating_feature instanceof LanguageFeature) {
    const language_feature = obj.floating_feature;
    if (language_feature.count > 1) {
      console.log(obj);
      console.log(obj.feature);
      console.log(obj.floating_feature);
    }
    const character_language_feature = obj.feature_options[0] as CharacterLanguageFeature;
    if (language_feature.type === "Specific") {
      if (language_feature.language_ids.length === language_feature.count) {
        return (<DisplayObjects type="language" ids={language_feature.language_ids} />);
      } else {
        let language_options = LANGUAGE_OPTIONS;
        if (language_feature.language_ids.length > 0) {
          language_options = language_options.filter(o => language_feature.language_ids.includes(o.id));
        }
        const known_ids = character.mind.languages_known.filter(id => 
          !character_language_feature.language_ids.includes(id));
        language_options = language_options.filter(o => !known_ids.includes(o.id));
        if (language_feature.count === 1) {
          // console.log(character_language_feature.language_ids);
          return (
            <SelectStringBox
              name={obj.name}
              options={language_options}
              value={character_language_feature.language_ids.length === 0 ? "" : character_language_feature.language_ids[0]}
              color={color}
              // log_it
              onChange={(changed: string) => {
                character_language_feature.language_ids = [changed];
                onChange(character_language_feature);
                setPing(!ping);
              }}
            />
          );
        } else {
          return (
            <SelectStringBox
              name={obj.name}
              options={language_options}
              values={character_language_feature.language_ids}
              color={color}
              multiple
              onChange={(changed: string[]) => {
                character_language_feature.language_ids = changed;
                onChange(character_language_feature);
                setPing(!ping);
              }}
            />
          );
        }
      }
    } else {
      // console.log(language_feature);
      if (language_feature.count === 1) {
        let language_options = LANGUAGE_OPTIONS;
        if (!ignore_type_limitations) {
          if (language_feature.type === "Standard") {
            const language_ids = LANGUAGES.filter(o => o.type === "Common" || o.type === "Exotic").map(o => o._id);
            language_options = language_options.filter(o => language_ids.includes(o.id));
          } else {
            const language_ids = LANGUAGES.filter(o => o.type === language_feature.type).map(o => o._id);
            language_options = language_options.filter(o => language_ids.includes(o.id));
          }
        }
        if (language_feature.language_ids.length > 0) {
          language_options = language_options.filter(o => language_feature.language_ids.includes(o.id));
        }
        const known_ids = character.mind.languages_known.filter(id => 
          !character_language_feature.language_ids.includes(id));
        language_options = language_options.filter(o => !known_ids.includes(o.id));
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 6
              }}>
              { !reloading &&
                <SelectStringBox
                  name={obj.name}
                  options={language_options}
                  value={character_language_feature.language_ids.length > 0 ? character_language_feature.language_ids[0] : "" } 
                  color={color}
                  // log_it
                  onChange={(changed: string) => {
                    character_language_feature.language_ids = [changed];
                    onChange(character_language_feature);
                    setPing(!ping);
                  }}
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <CheckBox
                name="Ignore Type Limits"
                value={ignore_type_limitations}
                onChange={(changed: boolean) => {
                  setIgnoreTypeLimitations(changed);
                  setReloading(true);
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        let language_options = LANGUAGE_OPTIONS;
        if (!ignore_type_limitations) {
          if (language_feature.type === "Standard") {
            const language_ids = LANGUAGES.filter(o => o.type === "Common" || o.type === "Exotic").map(o => o._id);
            language_options = language_options.filter(o => language_ids.includes(o.id));
          } else {
            const language_ids = LANGUAGES.filter(o => o.type === language_feature.type).map(o => o._id);
            language_options = language_options.filter(o => language_ids.includes(o.id));
          }
        }
        if (language_feature.language_ids.length > 0) {
          language_options = language_options.filter(o => language_feature.language_ids.includes(o.id));
        }
        const known_ids = character.mind.languages_known.filter(id => 
          !character_language_feature.language_ids.includes(id));
        language_options = language_options.filter(o => !known_ids.includes(o.id));
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 6
              }}>
              { !reloading &&
                <SelectStringBox
                  name={obj.name}
                  options={language_options}
                  values={character_language_feature.language_ids} 
                  color={color}
                  multiple
                  max_selected={language_feature.count}
                  onChange={(changed: string[]) => {
                    character_language_feature.language_ids = changed;
                    onChange(character_language_feature);
                    setPing(!ping);
                  }}
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <CheckBox
                name="Ignore Type Limits"
                value={ignore_type_limitations}
                onChange={(changed: boolean) => {
                  setIgnoreTypeLimitations(changed);
                  setReloading(true);
                }}
              />
            </Grid>
          </Grid>
        );
      }
    }
  }
  return <></>;
}
